import * as routes from "../constants/routes";
import debounce from "../scripts/debounce";
import Modal from "./Modal";
import React, {useEffect, useState} from "react";
import {useHistory} from "react-router";

const TIME_UNTIL_WARNING = 60000 * 14;
const TIME_UNTIL_REDIRECT = 60000 * 15;

const strings = {
    warningTitle: "Your application is about to time out.",
    warningDescription: "We will reset your application if you do not respond soon.",
    continueApplication: "Continue Application",
};

interface Focusable {
    focus: () => void;
}

type Props<T> = {
    children: React.ReactNode;
    focusAfterContinue: T | null;
    newApplicationRoute?: string;
    description?: string;
    noLink?: boolean;
};

export default function IdleTimeoutWrapper<T extends Focusable>(props: Props<T>) {
    const history = useHistory();
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        let warnTimeout: NodeJS.Timeout | null = null;
        let redirectTimeout: NodeJS.Timeout | null = null;

        function redirect() {
            history.replace(`${routes.TIMEOUT}${window.location.search}`, {
                to: props.newApplicationRoute,
                description: props.description,
                noLink: props.noLink,
            });
        }

        function clearTimeouts() {
            if (warnTimeout) {
                clearTimeout(warnTimeout);
            }
            if (redirectTimeout) {
                clearTimeout(redirectTimeout);
            }
        }

        function setTimeouts() {
            warnTimeout = setTimeout(() => setIsModalOpen(true), TIME_UNTIL_WARNING);
            redirectTimeout = setTimeout(redirect, TIME_UNTIL_REDIRECT);
        }

        function resetTimeouts() {
            clearTimeouts();
            setTimeouts();
        }

        const resetDebounce = debounce(resetTimeouts, 500);

        window.addEventListener("load", resetDebounce);
        window.addEventListener("mousemove", resetDebounce);
        window.addEventListener("mousedown", resetDebounce);
        window.addEventListener("click", resetDebounce);
        window.addEventListener("scroll", resetDebounce);
        window.addEventListener("keydown", resetDebounce);
        setTimeouts();
        return () => {
            window.removeEventListener("load", resetDebounce);
            window.removeEventListener("mousemove", resetDebounce);
            window.removeEventListener("mousedown", resetDebounce);
            window.removeEventListener("click", resetDebounce);
            window.removeEventListener("scroll", resetDebounce);
            window.removeEventListener("keydown", resetDebounce);
            clearTimeouts();
        };
    }, [history, props.newApplicationRoute, props.description, props.noLink]);

    return (
        <>
            {props.children}
            <Modal
                id="timeout-modal"
                label={strings.warningTitle}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={() => setIsModalOpen(false)}
                onSubmitLabel={strings.continueApplication}
                triggerElement={props.focusAfterContinue}
            >
                <div>{strings.warningTitle}</div>
                <div>{strings.warningDescription}</div>
            </Modal>
        </>
    );
}
